// Drag polar + lift curve for the high-lift foil. Two synced panels:
//   left  — CL vs CD (the drag polar)
//   right — CL vs alpha (the lift curve)
// One trace per series (e.g. flexfoil prior vs RANS truth vs Tailor surrogate).
// Clicking a marker reports (seriesIdx, ptIdx) so the caller can sync the flow slice.

import { useMemo } from "react";
import Plot from "react-plotly.js";
import { useHighliftTheme, type HighliftPalette } from "./useHighliftTheme";

const MONO_STACK = "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace";

/**
 * Themed Plotly base layout (paper/plot backgrounds, fonts, axis + grid colours)
 * built from the live light/dark palette so the polar/lift charts flip with the
 * global toggle. Mirrors the adaptive-sampling `basePlotlyLayout` shape, but
 * sourced from `useHighliftTheme()` instead of the dark-only token block.
 */
function basePlotlyLayout(
  palette: HighliftPalette,
  overrides: Record<string, unknown> = {},
): Record<string, unknown> {
  const axis = {
    gridcolor: palette.line,
    zerolinecolor: palette.line,
    linecolor: palette.ink4,
    color: palette.ink3,
    tickfont: { color: palette.ink3, size: 11 },
    title: { standoff: 12 },
  };
  return {
    paper_bgcolor: palette.bg,
    plot_bgcolor: palette.surface,
    font: { family: "Outfit, sans-serif", color: palette.ink, size: 12 },
    margin: { t: 24, b: 44, l: 56, r: 20 },
    xaxis: { ...axis },
    yaxis: { ...axis },
    hoverlabel: {
      bgcolor: palette.surface2,
      bordercolor: palette.blue,
      font: { family: "JetBrains Mono, monospace", color: palette.ink, size: 11 },
    },
    legend: {
      bgcolor: palette.surface,
      bordercolor: palette.line,
      borderwidth: 1,
      font: { color: palette.ink, size: 11 },
    },
    ...overrides,
  };
}

export interface PolarPoint {
  alpha: number;
  CL: number;
  CD: number;
  CM: number;
  /** Optional run-node id of the solved point — lets the scrubber sync the flow
   *  slice to the matching run. Absent for points with no resolvable node. */
  nodeId?: string;
}

export interface PolarSeries {
  label: string;
  color: string;
  points: PolarPoint[];
}

/** The CLmax point of a chosen series — derived from the solved CL-α points, not
 *  fabricated. Drives the larger "CLmax" marker on both panels and carries the
 *  separately-derived stall angle for the callout. */
export interface ClMaxMarker {
  seriesIdx: number;
  alpha: number;
  CL: number;
  CD: number;
  stallAlpha: number;
}

/**
 * Derive the CLmax point + stall angle of a series from its (already α-sorted)
 * solved CL-α points. CLmax = the max-CL point; stall α = the α where the lift
 * curve stops increasing — the first point whose CL is not greater than the prior
 * (slope sign flips ≤ 0), falling back to the global-max α. Returns null when the
 * series has fewer than 2 finite points (no curve to read). Pure + derived from
 * REAL solved data — never fabricated.
 */
export function deriveClMax(
  series: PolarSeries[],
  seriesIdx: number,
): ClMaxMarker | null {
  const s = series[seriesIdx];
  if (!s) return null;
  const pts = s.points.filter((p) => Number.isFinite(p.CL) && Number.isFinite(p.alpha));
  if (pts.length < 2) return null;
  // Scan forward for the first α where CL stops increasing (dCL/dα ≤ 0).
  let stallI: number | null = null;
  for (let i = 1; i < pts.length; i++) {
    if (pts[i]!.CL <= pts[i - 1]!.CL) {
      stallI = i;
      break;
    }
  }
  let maxI = 0;
  for (let i = 1; i < pts.length; i++) {
    if (pts[i]!.CL > pts[maxI]!.CL) maxI = i;
  }
  const p = pts[maxI]!;
  return {
    seriesIdx,
    alpha: p.alpha,
    CL: p.CL,
    CD: p.CD,
    stallAlpha: pts[stallI ?? maxI]!.alpha,
  };
}

export interface PolarPlotProps {
  series: PolarSeries[];
  /** Fired when a marker is clicked: the series index + point index within it. */
  onPick?: (seriesIdx: number, ptIdx: number) => void;
  height?: number;
  /** CLmax dot (α, CL, CD) on a chosen series — rendered as a larger marker on
   *  both the lift curve and the drag polar, plus a stall-α annotation. */
  clMax?: ClMaxMarker | null;
  /** Stall angle (deg) for the lift-curve callout. Usually `clMax.stallAlpha`. */
  stallAlpha?: number | null;
  /** Stall-progression scrubber: highlight this point of `scrubSeriesIdx` with a
   *  "current α" marker on both panels. Pure presentation; the slider lives in the
   *  caller. */
  scrubSeriesIdx?: number | null;
  scrubIdx?: number | null;
}

/**
 * CL–CD drag polar and CL–alpha lift curve. Each series renders as a connected
 * lines+markers trace in both panels with a consistent color. Click a point to
 * pick it (the caller resolves the matching flow slice).
 */
export function PolarPlot({
  series,
  onPick,
  height = 320,
  clMax = null,
  stallAlpha = null,
  scrubSeriesIdx = null,
  scrubIdx = null,
}: PolarPlotProps) {
  const palette = useHighliftTheme();

  const data = useMemo(() => {
    const traces: Record<string, unknown>[] = [];
    series.forEach((s, seriesIdx) => {
      // customdata packs [seriesIdx, ptIdx, alpha, CL, CD, CM] so onClick can map a
      // marker back to its series/point and the hover can read raw aero values.
      const hover =
        "α %{customdata[2]:.2f}°<br>C_L %{customdata[3]:.3f}<br>" +
        "C_D %{customdata[4]:.4f}<br>C_M %{customdata[5]:.3f}<extra>%{fullData.name}</extra>";
      // Drag polar — left panel (xaxis/yaxis).
      traces.push({
        type: "scatter",
        mode: "lines+markers",
        name: s.label,
        legendgroup: s.label,
        xaxis: "x",
        yaxis: "y",
        x: s.points.map((p) => p.CD),
        y: s.points.map((p) => p.CL),
        customdata: s.points.map((p, ptIdx) => [seriesIdx, ptIdx, p.alpha, p.CL, p.CD, p.CM]),
        line: { color: s.color, width: 2 },
        marker: { color: s.color, size: 6, line: { color: palette.surface, width: 1 } },
        hovertemplate: hover,
        connectgaps: false,
      });
      // Lift curve — right panel (xaxis2/yaxis2). Share legend group + hide its
      // legend entry so each series shows once in the legend.
      traces.push({
        type: "scatter",
        mode: "lines+markers",
        name: s.label,
        legendgroup: s.label,
        showlegend: false,
        xaxis: "x2",
        yaxis: "y2",
        x: s.points.map((p) => p.alpha),
        y: s.points.map((p) => p.CL),
        customdata: s.points.map((p, ptIdx) => [seriesIdx, ptIdx, p.alpha, p.CL, p.CD, p.CM]),
        line: { color: s.color, width: 2 },
        marker: { color: s.color, size: 6, line: { color: palette.surface, width: 1 } },
        hovertemplate: hover,
        connectgaps: false,
      });
    });
    // CLmax dot — a larger ringed marker on BOTH panels (lift curve: α×CL on
    // xaxis2/yaxis2; drag polar: CD×CL on xaxis/yaxis). Derived from the solved
    // CL-α points by the caller, not fabricated.
    if (clMax && Number.isFinite(clMax.CL)) {
      const clMaxMarker = {
        size: 12,
        color: "rgba(0,0,0,0)",
        line: { color: palette.warn, width: 2.5 },
        symbol: "circle",
      };
      const clMaxHover = `CLmax C_L %{y:.3f}<br>α %{customdata:.2f}°<extra>CLmax</extra>`;
      if (Number.isFinite(clMax.CD)) {
        traces.push({
          type: "scatter", mode: "markers", name: "CLmax",
          xaxis: "x", yaxis: "y", showlegend: false,
          x: [clMax.CD], y: [clMax.CL], customdata: [clMax.alpha],
          marker: clMaxMarker, hovertemplate: clMaxHover,
        });
      }
      traces.push({
        type: "scatter", mode: "markers", name: "CLmax",
        xaxis: "x2", yaxis: "y2", showlegend: false,
        x: [clMax.alpha], y: [clMax.CL], customdata: [clMax.alpha],
        marker: clMaxMarker, hovertemplate: clMaxHover,
      });
    }
    // Stall-progression scrubber — a filled "current α" marker on both panels at
    // the scrubbed point of the chosen series.
    if (scrubSeriesIdx != null && scrubIdx != null) {
      const s = series[scrubSeriesIdx];
      const p = s?.points[scrubIdx];
      if (p && Number.isFinite(p.CL)) {
        const scrubMarker = {
          size: 11, color: palette.blue,
          line: { color: palette.surface, width: 1.5 }, symbol: "circle",
        };
        const scrubHover = `α %{customdata:.2f}°<br>C_L %{y:.3f}<extra>current</extra>`;
        traces.push({
          type: "scatter", mode: "markers", name: "current",
          xaxis: "x", yaxis: "y", showlegend: false,
          x: [p.CD], y: [p.CL], customdata: [p.alpha],
          marker: scrubMarker, hovertemplate: scrubHover,
        });
        traces.push({
          type: "scatter", mode: "markers", name: "current",
          xaxis: "x2", yaxis: "y2", showlegend: false,
          x: [p.alpha], y: [p.CL], customdata: [p.alpha],
          marker: scrubMarker, hovertemplate: scrubHover,
        });
      }
    }
    return traces;
  }, [series, palette, clMax, scrubSeriesIdx, scrubIdx]);

  const layout = useMemo(() => {
    const stallPoint =
      stallAlpha != null && clMax
        ? series[clMax.seriesIdx]?.points.find((p) => p.alpha === stallAlpha && Number.isFinite(p.CL))
        : null;
    const base = basePlotlyLayout(palette, {
      height,
      showlegend: true,
      legend: { orientation: "h", y: -0.18, x: 0, font: { color: palette.ink, size: 10 } },
      margin: { t: 28, b: 56, l: 56, r: 20 },
      grid: { rows: 1, columns: 2, pattern: "independent", xgap: 0.14 },
    });
    return {
      ...base,
      xaxis: {
        ...(base.xaxis as object),
        title: { text: "C_D", font: { color: palette.ink3, size: 11 } },
      },
      yaxis: {
        ...(base.yaxis as object),
        title: { text: "C_L", font: { color: palette.ink3, size: 11 } },
      },
      xaxis2: {
        ...(base.xaxis as object),
        title: { text: "α (deg)", font: { color: palette.ink3, size: 11 } },
      },
      yaxis2: {
        ...(base.yaxis as object),
        title: { text: "C_L", font: { color: palette.ink3, size: 11 } },
      },
      annotations: [
        {
          text: "drag polar",
          xref: "paper",
          yref: "paper",
          x: 0.0,
          y: 1.04,
          xanchor: "left",
          yanchor: "bottom",
          showarrow: false,
          font: { color: palette.ink3, size: 11, family: MONO_STACK },
        },
        {
          text: "lift curve",
          xref: "paper",
          yref: "paper",
          x: 1.0,
          y: 1.04,
          xanchor: "right",
          yanchor: "bottom",
          showarrow: false,
          font: { color: palette.ink3, size: 11, family: MONO_STACK },
        },
        // Stall-α callout on the lift curve (xaxis2/yaxis2), derived from the
        // solved CL-α data separately from the CLmax marker.
        ...(stallPoint && stallAlpha != null
          ? [
              {
                text: `stall α ${stallAlpha.toFixed(1)}°`,
                xref: "x2",
                yref: "y2",
                x: stallPoint.alpha,
                y: stallPoint.CL,
                ax: 0,
                ay: -28,
                showarrow: true,
                arrowhead: 2,
                arrowsize: 0.9,
                arrowwidth: 1.2,
                arrowcolor: palette.warn,
                font: { color: palette.warn, size: 10, family: MONO_STACK },
                bgcolor: palette.surface,
                bordercolor: palette.warn,
                borderwidth: 1,
                borderpad: 2,
              },
            ]
          : []),
      ],
    };
  }, [height, palette, series, stallAlpha, clMax]);

  const config = useMemo(() => ({ displayModeBar: false, responsive: true }), []);

  return (
    <Plot
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data={data as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      layout={layout as any}
      config={config}
      useResizeHandler
      style={{ width: "100%", height }}
      onClick={(event) => {
        if (!onPick) return;
        const point = event?.points?.[0] as { customdata?: unknown } | undefined;
        const cd = point?.customdata as [number, number] | undefined;
        if (cd && Number.isFinite(cd[0]) && Number.isFinite(cd[1])) onPick(cd[0], cd[1]);
      }}
    />
  );
}
